import { formatDateToISOWeek, weeksInISOYear } from '@util/date-util'

import type { GeblokkeerdeWeek } from '../../../planning/types/GeblokkeerdeWeek'
import type { HoofdleidingProject } from '../../project/types/HoofdleidingProject'

export {
  planningProcesstapDict,
  planningProcesstappen,
  getDenseRangeDict,
  getAllCols,
  getCurrentWeekIndex,
  getProjectDisplayRange,
  getProjectCountPerWeek,
  indexGeblokkeerdeWeken,
  findGeblokkeerdeWeek,
  type GeblokkeerdeWekenIndex,
  type YearWeek,
}

const planningProcesstapDict = [
  { label: 'Intake', color: '#7C5BAE' },
  { label: 'Werkvoorbereiding', color: '#E47F18' },
  { label: 'In uitvoering', color: '#2A68B2' },
  { label: 'Oplevering', color: '#2E8B57' },
] as const

const planningProcesstappen: string[] = planningProcesstapDict.map(
  (p) => p.label,
)

type YearWeek = [number, number]
type ProjectRange = [YearWeek, YearWeek]

// Negative when a is before b, positive when after, 0 when equal
const compareYearWeek = ([yearA, weekA]: YearWeek, [yearB, weekB]: YearWeek) =>
  yearA - yearB || weekA - weekB

const nextYearWeek = ([year, week]: YearWeek): YearWeek =>
  week < weeksInISOYear(year) ? [year, week + 1] : [year + 1, 1]

// All weeks from start up to and including end
const getWeeksBetween = (start: YearWeek, end: YearWeek): YearWeek[] => {
  const weeks: YearWeek[] = []

  for (
    let current = start;
    compareYearWeek(current, end) <= 0;
    current = nextYearWeek(current)
  ) {
    weeks.push(current)
  }

  return weeks
}

// Parses an ISO week string like '2026-W05' into [2026, 5]
const parseYearWeek = (isoWeek: string): YearWeek => {
  const [year, week] = isoWeek.split('-W')

  return [Number(year), Number(week)]
}

const getProjectRange = ({
  startUitvoeringWeek,
  eindUitvoeringWeek,
}: HoofdleidingProject): ProjectRange | undefined => {
  if (!startUitvoeringWeek || !eindUitvoeringWeek) return undefined

  return [parseYearWeek(startUitvoeringWeek), parseYearWeek(eindUitvoeringWeek)]
}

const isWeekInRange = (yearWeek: YearWeek, [start, end]: ProjectRange) =>
  compareYearWeek(yearWeek, start) >= 0 && compareYearWeek(yearWeek, end) <= 0

// Range from the earliest start to the latest end of all planned projects
const getRange = (
  projects: HoofdleidingProject[],
): ProjectRange | undefined => {
  const ranges = projects
    .map(getProjectRange)
    .filter((range): range is ProjectRange => range !== undefined)

  if (ranges.length === 0) return undefined

  return ranges.reduce(([earliest, latest], [start, end]) => [
    compareYearWeek(start, earliest) < 0 ? start : earliest,
    compareYearWeek(end, latest) > 0 ? end : latest,
  ])
}

// Every week in the planning, grouped per year: { 2026: [50, 51, 52, 53], 2027: [1, 2] }
const getDenseRangeDict = (projects: HoofdleidingProject[]) => {
  const range = getRange(projects)

  if (!range) return {}

  const result: Record<number, number[]> = {}

  for (const [year, week] of getWeeksBetween(...range)) {
    result[year] = [...(result[year] ?? []), week]
  }

  return result
}

// Flattens the range dict into the planning's columns, in order
const getAllCols = (rangeDict: Record<number, number[]>): YearWeek[] =>
  Object.entries(rangeDict).flatMap(([year, weeks]) =>
    weeks.map((week): YearWeek => [Number(year), week]),
  )

// Index of the current week in the planning's columns, or -1 when the
// planning doesn't include it
const getCurrentWeekIndex = (allCols: YearWeek[], today = new Date()) => {
  const currentWeek = parseYearWeek(formatDateToISOWeek(today))

  return allCols.findIndex(
    (yearWeek) => compareYearWeek(yearWeek, currentWeek) === 0,
  )
}

// Splits the planning's columns into empty columns before, active columns
// during, and empty columns after the project
const getProjectDisplayRange = (
  project: HoofdleidingProject,
  allCols: YearWeek[],
) => {
  const projectRange = getProjectRange(project)

  if (!projectRange) {
    return { emptyColsBefore: 0, activeCols: [], emptyColsAfter: 0 }
  }

  const activeCols = allCols.filter((yearWeek) =>
    isWeekInRange(yearWeek, projectRange),
  )
  const firstActiveIndex = allCols.findIndex((yearWeek) =>
    isWeekInRange(yearWeek, projectRange),
  )
  const emptyColsBefore =
    firstActiveIndex === -1 ? allCols.length : firstActiveIndex
  const emptyColsAfter = allCols.length - emptyColsBefore - activeCols.length

  return { emptyColsBefore, activeCols, emptyColsAfter }
}

// Number of projects per week, keyed as '2026-5'
const getProjectCountPerWeek = (
  projects: HoofdleidingProject[],
): Record<string, number> => {
  const weekCounts: Record<string, number> = {}

  for (const project of projects) {
    const range = getProjectRange(project)

    if (!range) continue

    for (const [year, week] of getWeeksBetween(...range)) {
      const weekKey = `${year}-${week}`
      weekCounts[weekKey] = (weekCounts[weekKey] ?? 0) + 1
    }
  }

  return weekCounts
}

type GeblokkeerdeWekenIndex = Map<string, GeblokkeerdeWeek[]>

const geblokkeerdeWeekKey = ([year, week]: YearWeek, gebiedscode: string) =>
  `${year}-${week}|${gebiedscode}`

// Groups blocked weeks per week and gebied. Parsing the week also matches
// zero-padded weeks like '2026-W05'
const indexGeblokkeerdeWeken = (
  geblokkeerdeWeken: GeblokkeerdeWeek[],
): GeblokkeerdeWekenIndex => {
  const index: GeblokkeerdeWekenIndex = new Map()

  for (const blocked of geblokkeerdeWeken) {
    const key = geblokkeerdeWeekKey(
      parseYearWeek(blocked.week),
      blocked.gebiedscode,
    )
    index.set(key, [...(index.get(key) ?? []), blocked])
  }

  return index
}

// The blocked week for this project's gebied and one of its verantwoordelijken
const findGeblokkeerdeWeek = (
  index: GeblokkeerdeWekenIndex,
  yearWeek: YearWeek,
  { gebiedscode, verantwoordelijken }: HoofdleidingProject,
) =>
  index
    .get(geblokkeerdeWeekKey(yearWeek, gebiedscode))
    ?.find(({ organisatie }) => verantwoordelijken.includes(organisatie))
